import React, { useEffect, useState } from 'react';
import { Dialog, Box, Typography, IconButton, Chip, Grid, Stack, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ETransactionStatus, ITransaction } from 'common/transactions.interface';
import { formatDate } from 'utils/date';
import { AccountService, BankingService } from 'services';
import OtpModal from 'views/dashboard/private/Banking/OtpModal/OtpModal';
import { useSnackbar } from 'notistack';
import { IUserRegistry } from 'common/user.interface';

const bankingService = new BankingService();
const accountService = new AccountService();

type ApprovalStatus = 'APPROVED' | 'REJECTED' | 'PENDING';

type ApprovalInfo = {
  name: string;
  email: string;
  dateTime: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  transaction?: ITransaction;
  onApprovalChange?: () => void;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize={20} fontWeight={500} color="#8A8A8A">
    {children}
  </Typography>
);

const InfoCard = ({ highlighted, status, children }: { highlighted?: boolean; status?: ApprovalStatus; children: React.ReactNode }) => {
  const isRejected = status === 'REJECTED';

  let bgColor = '#F7F9FB';
  let borderColor = 'none';

  if (highlighted) {
    bgColor = isRejected ? '#FDEEEF' : '#F1FBF5';
    borderColor = isRejected ? '#D92D20' : '#0B7A3B';
  }

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: 3,
        backgroundColor: bgColor,
        border: highlighted ? `2px solid ${borderColor}` : 'none',
      }}
    >
      {children}
    </Box>
  );
};

const Label = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize={14} fontWeight={700} mb={0.5}>
    {children}
  </Typography>
);

const Value = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize={14} color="text.secondary">
    {children}
  </Typography>
);

const ApprovalDetailsModal = ({ open, onClose, transaction, onApprovalChange }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [reviewing, setReviewing] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<IUserRegistry | null>(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | null>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const loggedUserInfo = await accountService.getMe();
      setUserInfo(loggedUserInfo);
    };

    fetchUserInfo();
  }, []);

  const transactionStatus = transaction?.status;

  const inclusion: ApprovalInfo = {
    name: transaction?.createdBy?.name || 'N/A',
    email: transaction?.createdBy?.email || 'N/A',
    dateTime: transaction ? formatDate(transaction.createdAt) : '',
  };

  const isApproved =
    transactionStatus &&
    ![ETransactionStatus.REJECTED_APPROVAL, ETransactionStatus.PENDING_APPROVAL, ETransactionStatus.EXPIRED].includes(transactionStatus);

  const userId = userInfo?.id;
  const isInToReview = transaction?.toReviewEntries?.some((u) => u.person.id === userId);
  const isAlreadyReviewed = transaction?.reviewedEntries?.some((u) => u.person.id === userId);
  const canApprove = transactionStatus === ETransactionStatus.PENDING_APPROVAL && isInToReview && !isAlreadyReviewed;

  const handleApprove = () => {
    if (!transaction?.id) return;
    setPendingAction('approve');
    setShowOtpModal(true);
  };

  const handleReject = () => {
    if (!transaction?.id) return;
    setPendingAction('reject');
    setShowOtpModal(true);
  };

  const handleOtpConfirm = async (otp: string) => {
    if (!transaction?.id || !pendingAction) return;

    setShowOtpModal(false);
    setReviewing(true);

    try {
      if (pendingAction === 'approve') {
        await bankingService.approveTransaction(transaction.id, otp);
        enqueueSnackbar('Transação aprovada com sucesso!', { variant: 'success' });
      } else {
        await bankingService.rejectTransaction(transaction.id, otp);
        enqueueSnackbar('Transação recusada com sucesso.', { variant: 'success' });
      }
      onApprovalChange?.();
      onClose();
    } catch {
      enqueueSnackbar(pendingAction === 'approve' ? 'Erro ao aprovar transação.' : 'Erro ao recusar transação.', { variant: 'error' });
    } finally {
      setReviewing(false);
      setPendingAction(null);
    }
  };

  const handleOtpClose = () => {
    setShowOtpModal(false);
    setPendingAction(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 5,
            p: 5,
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box textAlign="center" flex={1}>
            <Typography fontSize={28} fontWeight={600}>
              Aprovação
            </Typography>
            <Typography fontSize={16} color="text.secondary" mt={1}>
              Veja aqui quem incluiu e aprovou a transação
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box mt={6}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <SectionTitle>Inclusão</SectionTitle>
          </Box>
          <InfoCard>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Label>Responsável</Label>
                <Value>
                  {inclusion.name} ({inclusion.email})
                </Value>
              </Grid>
              <Grid item xs={6}>
                <Label>Data e hora</Label>
                <Value>{inclusion.dateTime}</Value>
              </Grid>
            </Grid>
          </InfoCard>
        </Box>
        <Box mt={6}>
          <SectionTitle>
            Aprovadores
            {isApproved && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Aprovado"
                sx={{
                  fontWeight: 600,
                  color: '#0B7A3B',
                  borderColor: '#0B7A3B',
                  backgroundColor: '#E7F6ED',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  marginLeft: '8px',
                }}
                variant="outlined"
              />
            )}
            {transactionStatus === ETransactionStatus.REJECTED_APPROVAL && (
              <Chip
                icon={<CloseIcon />}
                label="Rejeitado"
                sx={{
                  fontWeight: 600,
                  color: '#D92D20',
                  borderColor: '#D92D20',
                  backgroundColor: '#FDEEEF',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  marginLeft: '8px',
                }}
                variant="outlined"
              />
            )}
            {transactionStatus === ETransactionStatus.EXPIRED && (
              <Chip
                icon={<AccessTimeIcon />}
                label="Expirado"
                sx={{
                  fontWeight: 600,
                  color: '#616161',
                  borderColor: '#616161',
                  backgroundColor: '#F5F5F5',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  marginLeft: '8px',
                }}
                variant="outlined"
              />
            )}
          </SectionTitle>
          <Stack spacing={3} mt={2}>
            {!transaction?.toReviewEntries?.length && !transaction?.reviewedEntries?.length ? (
              <Typography color="text.secondary" fontSize={14}>
                Nenhum aprovador encontrado (transação pode ter sido aprovada automaticamente)
              </Typography>
            ) : (
              <>
                {transaction?.toReviewEntries
                  ?.filter((pending) => !transaction?.reviewedEntries?.some((rev) => rev.person.id === pending.person.id))
                  .map((approver) => (
                    <Box key={approver.person.email}>
                      <InfoCard>
                        <Grid container spacing={4}>
                          <Grid item xs={6}>
                            <Label>Pendente</Label>
                            <Value>
                              {approver.person.name} ({approver.person.email})
                            </Value>
                          </Grid>
                        </Grid>
                      </InfoCard>
                    </Box>
                  ))}
                {transaction?.reviewedEntries?.map((approver) => (
                  <Box key={approver.person.email}>
                    <InfoCard highlighted status={approver.approved ? 'APPROVED' : 'REJECTED'}>
                      <Grid container spacing={4}>
                        <Grid item xs={6}>
                          <Label>{approver.approved ? 'Aprovado' : 'Rejeitado'} por</Label>
                          <Value>
                            {approver.person.name} ({approver.person.email})
                          </Value>
                        </Grid>
                      </Grid>
                    </InfoCard>
                  </Box>
                ))}
              </>
            )}
          </Stack>
        </Box>
        {canApprove && (
          <Box mt={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  sx={{ textTransform: 'none' }}
                  disableElevation
                  fullWidth
                  onClick={handleApprove}
                  disabled={reviewing}
                >
                  {reviewing && pendingAction === 'approve' ? 'Processando...' : 'Aprovar'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RemoveCircleIcon />}
                  sx={{ textTransform: 'none' }}
                  disableElevation
                  fullWidth
                  onClick={handleReject}
                  disabled={reviewing}
                >
                  {reviewing && pendingAction === 'reject' ? 'Processando...' : 'Recusar'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Dialog>
      <OtpModal show={showOtpModal} onClose={handleOtpClose} onConfirm={handleOtpConfirm} />
    </>
  );
};

export default ApprovalDetailsModal;
